"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Mail, Twitter, Linkedin, ArrowRight, Loader } from "lucide-react";
function FooterQuickLinks() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-24 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-3">
        <li data-index={0}><Link to="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">Home</Link></li>
        <li data-index={1}><Link to="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">Premium Catalog</Link></li>
        <li data-index={2}><Link to="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">COA Library</Link></li>
        <li data-index={3}><Link to="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">About Us</Link></li>
        <li data-index={4}><Link to="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">Contact</Link></li>
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-sm text-muted-foreground hover:text-primary transition-colors">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-4 bg-muted/50 border border-border rounded-md">
        <p className="text-sm text-foreground font-medium">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="email" rules={{
      required: "Email is required for updates",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <div className="relative">
                <input type="email" placeholder="Enter your email address" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pr-12" {...field} />
                <Button type="submit" size="icon" variant="ghost" className="absolute right-0 top-0 h-10 w-10 text-muted-foreground hover:text-primary" disabled={isSubmitting}>
                  {isSubmitting ? <Loader className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
                  <span className="sr-only">Subscribe</span>
                </Button>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />
      {submitError && <p className="text-sm text-destructive">{submitError}</p>}
      <p className="text-xs text-muted-foreground leading-relaxed">
        Subscribe to receive updates on new product syntheses, COA releases, and exclusive research incentives.
      </p>
    </Form>;
}
function SocialLinks() {
  return <div className="flex items-center gap-4">
      <Link to="mailto:info@signalsourceone.com" className="text-muted-foreground hover:text-primary transition-colors">
        <Mail className="size-5" />
        <span className="sr-only">Email Us</span>
      </Link>
      <Link to="#" className="text-muted-foreground hover:text-primary transition-colors">
        <Twitter className="size-5" />
        <span className="sr-only">Twitter</span>
      </Link>
      <Link to="#" className="text-muted-foreground hover:text-primary transition-colors">
        <Linkedin className="size-5" />
        <span className="sr-only">LinkedIn</span>
      </Link>
    </div>;
}
export default function ComplianceResearchFooter() {
  const legalLinks = [];
  return <section id="compliance-research-footer" className="w-full bg-background flex flex-col border-t border-border">
      <div className="max-w-7xl mx-auto w-full px-6 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          
          {}
          <div className="flex flex-col items-start">
            <img src="https://supreme-hookworm.10web.cloud/wp-content/uploads/2026/04/signal-source-one-logo-stacked.png" alt="Signal Source One" className="h-32 mb-6" />
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              Providing premium, laboratory-grade peptide products for scientific advancement. Strict quality control and third-party verification for uncompromising research integrity.
            </p>
            <div className="mt-8">
              <SocialLinks />
            </div>
          </div>

          {}
          <div>
            <h3 className="text-xs font-semibold text-foreground tracking-widest uppercase mb-6">
              Navigation
            </h3>
            <MenuProvider menu_id="61">
              <FooterQuickLinks />
            </MenuProvider>
          </div>

          {}
          <div>
            <h3 className="text-xs font-semibold text-foreground tracking-widest uppercase mb-6">
              Compliance & Legal
            </h3>
            <ul className="space-y-3">
              {legalLinks.map((link, i) => <li key={i} data-index={i}>
                  <Link to={link.path} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    {link.title}
                  </Link>
                </li>)}
              <li>
                <Link to="#" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Shipping Policy
                </Link>
              </li>
            </ul>
          </div>

          {}
          <div>
            <h3 className="text-xs font-semibold text-foreground tracking-widest uppercase mb-6">
              Research Updates
            </h3>
            <FormProvider formId="footer-newsletter-signup" sectionName="Footer Newsletter" defaultValues={{
            email: ""
          }} submitText="Subscribe" successMessage="Subscription confirmed. Welcome to Signal Source One updates.">
              <NewsletterForm />
            </FormProvider>
          </div>

        </div>
      </div>

      {}
      <div className="w-full bg-secondary text-secondary-foreground py-6 px-6 mt-auto border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-xs font-medium tracking-wider uppercase text-center md:text-left w-full text-secondary-foreground/60 leading-relaxed">
            RESEARCH USE ONLY — Strictly prohibited for human consumption or therapeutic application.
          </p>
          <p className="text-xs text-secondary-foreground/70 shrink-0">
            &copy; {new Date().getFullYear()} Signal Source One. All rights reserved.
          </p>
        </div>
      </div>
    </section>;
}